import React from 'react';
import { parse } from 'query-string';
import { Redirect, RouteComponentProps, RouteProps } from 'react-router-dom';

import {
  TermsPage,
  PageContentContainer,
  AdminTermsEdit,
  AdminTermsOverview,
  CreatePage,
  PreviewPageContainer,
  DataProtectionTalentManager,
  DataProtectionTalentPoolManager,
  DataProtectionFreelancerManager,
} from './pages';
import { PageDetails } from './models/page';
import { MetaLegalPages, MetaTermsAndConditions } from './pages/Meta';

export type TRouteNameSpace = 'public' | 'admin';
export type RoutesConfigInterface = Readonly<
  Record<TRouteNameSpace, Record<string, RouteProps>>
>;

const mountWithRoute = <T extends { [K in keyof T]?: string }>(
  Element: React.FC<RouteComponentProps<T>>
): React.FC<RouteComponentProps> => {
  const routeMounter = (props: RouteComponentProps<T>): React.ReactElement => (
    <Element {...props} />
  );

  return routeMounter;
};

export const RoutesConfig = {
  /* #region public routes */
  public: {
    publicTermsOverview: {
      path: '/terms',
      exact: true,
      component: TermsPage,
    },
    publicTermDetails: {
      path: '/terms/:slug',
      exact: true,
      component: mountWithRoute<{ slug: string }>(({ match }) => (
        <PageContentContainer
          renderMetaComponent={(pageDetails: PageDetails) => (
            <MetaTermsAndConditions pageDetails={pageDetails} />
          )}
          slug={match.params.slug}
        />
      )),
    },
    imprint: {
      path: '/legalnotice',
      exact: true,
      component: mountWithRoute(() => (
        <PageContentContainer
          showHeader={false}
          showLiveDate={false}
          slug={'legalnotice'}
          renderMetaComponent={(pageDetails: PageDetails) => (
            <MetaLegalPages pageDetails={pageDetails} />
          )}
        />
      )),
    },
    dataProcessingPolicyDE: {
      path: '/support/data-processing-policy',
      exact: true,
      component: () => <Redirect to="/legal/data-processing-policy" />,
    },
    dataProcessingPolicyEN: {
      path: '/support/data-processing-policy/en',
      exact: true,
      component: () => <Redirect to="/legal/data-processing-policy" />,
    },
    dataProtectionTalentManager: {
      path: '/legal/privacy_policy_talentmanager',
      exact: true,
      component: DataProtectionTalentManager,
    },
    dataProtectionTalentPoolManager: {
      path: '/legal/privacy_policy_talentpoolmanager',
      exact: true,
      component: DataProtectionTalentPoolManager,
    },
    dataProtectionFreelancerManager: {
      path: '/legal/privacy_policy_freelancermanager',
      exact: true,
      component: DataProtectionFreelancerManager,
    },
    legalPages: {
      path: '/legal/:slug',
      exact: true,
      component: mountWithRoute<{ slug: string }>(({ match }) => (
        <PageContentContainer
          showHeader={false}
          showLiveDate={false}
          slug={match.params.slug}
          renderMetaComponent={(pageDetails: PageDetails) => (
            <MetaLegalPages pageDetails={pageDetails} />
          )}
        />
      )),
    },
  },
  /* #regionend public routes */

  /* #region Admin routes */
  admin: {
    adminTermsOverview: {
      path: '/admin/legal/terms_and_conditions',
      exact: true,
      component: AdminTermsOverview,
    },
    adminTermsEdit: {
      path: '/admin/legal/terms_and_conditions/:slug/:versionId/:locale/edit',
      exact: true,
      component: mountWithRoute<{
        slug: string;
        versionId: string;
        locale: string;
      }>(
        ({
          match: {
            params: { slug, versionId, locale },
          },
          location: { search },
        }): React.ReactElement => {
          const genericVersionId =
            parseInt(parse(search)['genericVersionId'] as string) || null;
          return (
            <AdminTermsEdit
              slug={slug}
              versionId={parseInt(versionId)}
              genericVersionId={genericVersionId}
              locale={locale}
            />
          );
        }
      ),
    },
    adminTermsCreateNew: {
      path: '/admin/legal/terms_and_conditions/new',
      exact: true,
      component: CreatePage,
    },
    previewPageShow: {
      path: '/admin/legal/terms_and_conditions/preview/:versionId/:locale',
      exact: true,
      component: mountWithRoute<{ versionId: string; locale: string }>(
        ({ match, location: { search } }): React.ReactElement => {
          const genericVersionId =
            parseInt(parse(search)['genericVersionId'] as string) || null;
          return (
            <PreviewPageContainer
              versionId={parseInt(match.params.versionId)}
              genericVersionId={genericVersionId}
              locale={match.params.locale}
            />
          );
        }
      ),
    },
  },
  /* #regionend admin routes */
};

export const AppRoutes = {
  public: {
    publicTermDetails: (slug: string): string => {
      return `/terms/${slug}`;
    },
  },
  admin: {
    adminTermsOverview: (
      slug: string,
      versionId: number,
      locale: string,
      genericVersionId = null
    ): string => {
      const queryParam = genericVersionId
        ? `?genericVersionId=${genericVersionId}`
        : '';

      return `/admin/legal/terms_and_conditions/${slug}/${versionId}/${locale}/edit${queryParam}`;
    },
    previewPageShow: (
      versionId: number,
      locale: string,
      genericVersionId = null
    ): string => {
      const queryParam = genericVersionId
        ? `?genericVersionId=${genericVersionId}`
        : '';
      return `/admin/legal/terms_and_conditions/preview/${versionId}/${locale}${queryParam}`;
    },
  },
};
